import type { SpotDto } from "@boomap/contracts";
import { useMemo, useState } from "react";

import { useI18n } from "../i18n/index.ts";
import { freshnessOf } from "../lib/freshness.ts";
import { SpotIcon } from "./spot-icon.tsx";

export interface SpotSheetProps {
  readonly spot: SpotDto;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onReport: () => void;
  readonly onRetract: () => void;
  readonly onTakeDown: () => void;
  readonly onClose: () => void;
}

export const SpotSheet = (props: SpotSheetProps): React.ReactElement => {
  const { locale, t } = useI18n();
  const [confirmingTakeDown, setConfirmingTakeDown] = useState(false);

  // L'heure suit la langue : « 22:35 » en français, « 10:35 PM » en anglais.
  const timeFormat = useMemo(
    () => new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" }),
    [locale],
  );

  /**
   * Fraîcheur de l'information, calculée au rendu de la feuille. C'est ce qui
   * compte le soir d'Halloween : « des bonbons » vu il y a trois heures ne veut
   * plus rien dire. Le délai absolu reste en infobulle.
   */
  const freshness = useMemo(() => {
    const relative = freshnessOf({ updatedAt: new Date(props.spot.updatedAt), now: new Date() });

    if (relative === null) {
      return t.spot.justUpdated;
    }

    const format = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

    return t.spot.updated(format.format(relative.value, relative.unit));
  }, [props.spot.updatedAt, locale, t]);

  if (confirmingTakeDown) {
    return (
      <section className="sheet" aria-labelledby="takedown-title">
        <header className="sheet__header">
          <h2 id="takedown-title">{t.spot.takeDown.title}</h2>
          <button
            type="button"
            className="sheet__close"
            onClick={() => setConfirmingTakeDown(false)}
            aria-label={t.spot.takeDown.cancel}
          >
            ✕
          </button>
        </header>

        <p className="sheet__hint">{t.spot.takeDown.effect}</p>
        <p className="sheet__note sheet__note--warning">{t.spot.takeDown.attestation}</p>

        {props.error !== null && (
          <p className="alert" role="alert">
            {props.error}
          </p>
        )}

        <div className="sheet__actions">
          <button
            type="button"
            className="button button--ghost"
            onClick={() => setConfirmingTakeDown(false)}
          >
            {t.spot.takeDown.cancel}
          </button>
          <button
            type="button"
            className="button button--primary"
            disabled={props.busy}
            onClick={props.onTakeDown}
          >
            {props.busy ? t.spot.takeDown.submitting : t.spot.takeDown.confirm}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="sheet" aria-labelledby="spot-title">
      <header className="sheet__header">
        <h2 id="spot-title" className="sheet__title">
          <SpotIcon treats={props.spot.treats} size={28} />
          {t.treats[props.spot.treats].label}
        </h2>
        <button
          type="button"
          className="sheet__close"
          onClick={props.onClose}
          aria-label={t.spot.closeLabel}
        >
          ✕
        </button>
      </header>

      <p className="sheet__hint">{t.treats[props.spot.treats].hint}</p>

      {props.spot.note !== null && (
        <blockquote className="sheet__quote">{props.spot.note}</blockquote>
      )}

      <p className="sheet__note">
        <span title={t.spot.declaredAt(timeFormat.format(new Date(props.spot.declaredAt)))}>
          {freshness}
        </span>
        {props.spot.reportCount > 0 && ` · ${t.spot.reports(props.spot.reportCount)}`}
      </p>

      {props.error !== null && (
        <p className="alert" role="alert">
          {props.error}
        </p>
      )}

      <div className="sheet__actions">
        {/* Sur ses propres pastilles, supprimer remplace signaler : se
            signaler soi-même n'aurait aucun sens. */}
        {props.spot.mine ? (
          <button
            type="button"
            className="button button--ghost"
            disabled={props.busy}
            onClick={props.onRetract}
          >
            {props.busy ? t.spot.retracting : t.spot.retract}
          </button>
        ) : (
          <button
            type="button"
            className="button button--ghost"
            disabled={props.busy}
            onClick={props.onReport}
          >
            {props.busy ? t.spot.reporting : t.spot.report}
          </button>
        )}
        <button type="button" className="button button--primary" onClick={props.onClose}>
          {t.spot.close}
        </button>
      </div>

      {/*
        Le recours de l'occupant, discret mais toujours accessible : il n'a pas à
        chercher, et il n'a besoin de l'accord de personne.

        Absent de ses **propres** pastilles : leur auteur les supprime, ce qui est
        plus simple et ne bloque pas l'emplacement pour la saison. Proposer les
        deux ne laissait qu'une question — laquelle choisir.
      */}
      {!props.spot.mine && (
        <button
          type="button"
          className="sheet__takedown"
          onClick={() => setConfirmingTakeDown(true)}
        >
          {t.spot.takeDownLink}
        </button>
      )}
    </section>
  );
};
